import { NamedComplexEnum } from '../../utils/complexEnum/NamedComplexEnum'
import { CIMSPage, CIMSPageItem } from '../cimsPage'
import {
  ConcretePackets,
  ConstructionAdminSubmittals,
  DesignSubmittals,
  FieldDesignChanges,
  Issues,
  RequestForInformation,
} from './SharePointParentFolderItem'

/**
 * Contains regular expressions and error messages to be used when validating
 * SharePoint folder identifiers
 */
export const SharePointRegexValidation = {
  /**
   * Checks for invalid characters
   *
   * Invalid characters: * " : < > ? / \ |
   */
  invalidChars: {
    regex: RegExp(/^[^*"#:<>?/\\|]*$/),
    message:
      'Invalid character(s) found in SharePoint reference: * " # : < > ? / \\ |' as const,
  },
  /**
   * Checks for leading, trailing, or only whitespace
   *
   * Allows empty strings
   */
  whitespace: {
    regex: RegExp(/(^|(^\S)|(^\S)(.*)([\S]))$/),
    message: 'Cannot contain leading, trailing, or only whitespace' as const,
  },
  /**
   * Checks for both invalid characters and leading, trailing, or only whitespace
   *
   * Invalid characters: * " : < > ? / \ |
   *
   * Allows empty strings
   */
  fullValidation: {
    regex: RegExp(
      /(^|(^[^*"#:<>?/\\|\s])|(^[^*"#:<>?/\\|\s])([^*"#:<>?/\\|]*)([^*"#:<>?/\\|\s]))$/
    ),
    message: 'Invalid SharePoint identifier' as const,
  },
}

export interface SharePointFolderReference {
  /**
   * the parent folder where the {@link SharePointFolderReference.folder|folder}
   * is located
   *
   * @see {@link SharePointParentFolder}
   */
  parent: SharePointParentFolderItem
  /**
   * the name of the folder under {@link SharePointFolderReference.parent|parentFolder}
   * in SharePoint
   *
   * @example
   * const constructionSubmittalFolder = constructionAdminSubmittal.sharePointFolderName
   * const concreteFolder = concreteTestSet.testSetId
   * const designSubmittalFolder = designSubmittal.sharePointFolderName
   * const fieldDesignChangeFolder = fieldDesignChange.number
   * const requestForInformationFolder = requestForInformation.number
   */
  folder: string
}

export type SharePointParentFolderItem =
  | typeof ConcretePackets
  | typeof ConstructionAdminSubmittals
  | typeof DesignSubmittals
  | typeof FieldDesignChanges
  | typeof RequestForInformation
  | typeof Issues
type ID = SharePointParentFolderItem['id']
type Name = SharePointParentFolderItem['name']
class SharePointParentFolderEnum extends NamedComplexEnum<
  'SharePointParentFolder',
  SharePointParentFolderItem,
  Name,
  ID
> {
  readonly typename = 'SharePointParentFolder'
  fromClassType(classType: string) {
    return this.find((item) => item.classType === classType)
  }
  fromClassTypeOrFail(classType: string) {
    return this.findOrFail((item) => item.classType === classType)
  }
  /**
   * returns all {@link CIMSModule} enums associated with the enums
   */
  get modules() {
    return this.map(({ module }) => module)
  }
  /**
   * returns all {@link name} values associated with the enums
   */
  get names() {
    return this.map(({ name }) => name)
  }
  /**
   * attempts to find an enum by a {@link CIMSPageItem}
   */
  fromPage(page: CIMSPageItem) {
    if (page === CIMSPage.DSLog) return DesignSubmittals
    else if (page === CIMSPage.ConcreteTestSets) return ConcretePackets
    else if (page === CIMSPage.CASTracker) return ConstructionAdminSubmittals
    else if (page === CIMSPage.FDCTracker) return FieldDesignChanges
    else if (page === CIMSPage.RFITracker) return RequestForInformation
    else if (page === CIMSPage.IssueTracker) return Issues
    else return undefined
  }
  get values() {
    return [
      this.ConcretePackets,
      this.ConstructionAdminSubmittals,
      this.DesignSubmittals,
      this.FieldDesignChanges,
      this.RequestForInformation,
      this.Issues,
    ]
  }
  readonly ConcretePackets = ConcretePackets
  readonly ConstructionAdminSubmittals = ConstructionAdminSubmittals
  readonly DesignSubmittals = DesignSubmittals
  readonly FieldDesignChanges = FieldDesignChanges
  readonly RequestForInformation = RequestForInformation
  readonly Issues = Issues
}

export const SharePointParentFolder = new SharePointParentFolderEnum()
